import { useState } from 'react';
import type { PendingQuestion } from '@shared/types';
import { Button } from '@/components/ui/button';
import { MessageCircleQuestion, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Props {
  question: PendingQuestion;
  disabled?: boolean;
  onAnswer: (answer: string) => void;
}

/** Dedicated prompt that appears when the agent called ask_user. Submitting
 *  the answer creates a new user message that continues the conversation. */
export function AskUserPrompt({ question, disabled, onAnswer }: Props) {
  const { t } = useTranslation('common');
  const [draft, setDraft] = useState('');

  const submit = (answer: string) => {
    const trimmed = answer.trim();
    if (!trimmed) return;
    onAnswer(trimmed);
  };

  return (
    <div className="rounded-lg border border-synergy-blue/40 bg-synergy-blue/5 p-3 space-y-3">
      <div className="flex items-start gap-2">
        <MessageCircleQuestion className="h-4 w-4 text-synergy-blue shrink-0 mt-0.5" />
        <div className="flex-1 text-sm text-synergy-text-primary whitespace-pre-wrap">
          {question.question}
        </div>
      </div>

      {question.suggestions && question.suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {question.suggestions.map((s, i) => (
            <Button
              key={i}
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => submit(s)}
              className="h-7 px-2.5 text-xs"
            >
              {s}
            </Button>
          ))}
        </div>
      )}

      {(question.allowFreeText ?? true) && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(draft);
          }}
          className="flex gap-1.5"
        >
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={disabled}
            placeholder={t('chat.freeAnswerPlaceholder')}
            className="flex-1 rounded-md border border-synergy-neutral-800 bg-synergy-neutral-950 px-2.5 py-1.5 text-xs text-synergy-text-primary placeholder:text-synergy-neutral-500 focus:outline-none focus:border-synergy-blue/60"
          />
          <Button
            type="submit"
            size="sm"
            disabled={disabled || !draft.trim()}
            className="h-8 px-2.5"
          >
            <Send className="h-3 w-3" />
          </Button>
        </form>
      )}
    </div>
  );
}
